import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';

interface Badge {
  name: string;
  icon: string;
  description: string;
}

export default function TrustBadges() {
  const [badges, setBadges] = useState<Badge[]>([]);
  const [editingField, setEditingField] = useState<{ index: number; field: string } | null>(null);
  const [editValue, setEditValue] = useState('');

  // Load badges from localStorage or use defaults
  useEffect(() => {
    const saved = localStorage.getItem('trustBadges');
    if (saved) {
      setBadges(JSON.parse(saved));
    } else {
      const defaults: Badge[] = [
        {
          name: 'DOT Certified',
          icon: 'ri-government-line',
          description: 'Click to edit',
        },
        {
          name: 'Fully Insured',
          icon: 'ri-shield-check-line',
          description: 'Click to edit',
        },
        {
          name: 'BBB Accredited',
          icon: 'ri-star-line',
          description: 'Click to edit',
        },
        {
          name: 'Safety First',
          icon: 'ri-shield-star-line',
          description: 'Click to edit',
        },
        {
          name: 'Licensed Drivers',
          icon: 'ri-user-star-line',
          description: 'Click to edit',
        },
        {
          name: 'Bonded',
          icon: 'ri-file-shield-line',
          description: 'Click to edit',
        },
        {
          name: 'FMCSA Registered',
          icon: 'ri-file-list-3-line',
          description: 'Click to edit',
        },
        {
          name: 'Eco-Friendly',
          icon: 'ri-leaf-line',
          description: 'Click to edit',
        },
      ];
      setBadges(defaults);
      localStorage.setItem('trustBadges', JSON.stringify(defaults));
    }
  }, []);

  const saveBadges = (updated: Badge[]) => {
    setBadges(updated);
    localStorage.setItem('trustBadges', JSON.stringify(updated));
  };

  const startEdit = (index: number, field: string, currentValue: string) => {
    setEditingField({ index, field });
    setEditValue(currentValue);
  };

  const saveEdit = () => {
    if (editingField === null) return;
    
    const updated = [...badges];
    updated[editingField.index] = {
      ...updated[editingField.index],
      [editingField.field]: editValue,
    };
    
    saveBadges(updated);
    setEditingField(null);
    setEditValue('');
  };

  const cancelEdit = () => {
    setEditingField(null);
    setEditValue('');
  };

  const resetToDefaults = () => {
    if (confirm('Reset all trust badges to default values?')) {
      localStorage.removeItem('trustBadges');
      window.location.reload();
    }
  };

  const renderEditableField = (index: number, field: string, value: string, className: string = '') => {
    const isEditing = editingField?.index === index && editingField?.field === field;
    
    if (isEditing) {
      return (
        <div className="flex items-center space-x-2" onClick={(e) => e.stopPropagation()}>
          <input
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') saveEdit();
              if (e.key === 'Escape') cancelEdit();
            }}
            className="bg-white/20 border border-electric-blue text-white px-2 py-1 rounded text-sm flex-1"
            autoFocus
          />
          <button onClick={saveEdit} className="text-green-400 hover:text-green-300 cursor-pointer">
            <i className="ri-check-line w-4 h-4 flex items-center justify-center"></i>
          </button>
          <button onClick={cancelEdit} className="text-red-400 hover:text-red-300 cursor-pointer">
            <i className="ri-close-line w-4 h-4 flex items-center justify-center"></i>
          </button>
        </div>
      );
    }
    
    return (
      <div
        onClick={(e) => {
          e.stopPropagation();
          startEdit(index, field, value);
        }}
        className={`${className} cursor-pointer hover:bg-white/5 rounded px-2 py-1 transition-all duration-200 group relative`}
      >
        {value}
        <i className="ri-pencil-line absolute -right-6 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 text-electric-blue text-xs w-4 h-4 flex items-center justify-center"></i>
      </div>
    );
  };

  return (
    <section className="py-24 bg-gradient-to-b from-dark-800 to-dark-900 relative overflow-hidden">
      <div className="absolute inset-0">
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-electric-blue/5 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="font-orbitron text-5xl font-bold text-white mb-4">
            TRUST <span className="text-electric-blue">BADGES</span>
          </h2>
          <p className="font-inter text-white/60 text-lg mb-4">
            Certified, verified, and trusted by industry leaders
          </p>
          <button
            onClick={resetToDefaults}
            className="text-white/40 hover:text-electric-blue text-sm font-inter transition-colors duration-200 cursor-pointer mb-8"
          >
            <i className="ri-refresh-line mr-2 w-4 h-4 inline-flex items-center justify-center"></i>
            Reset to Defaults
          </button>

          {/* Center Badge */}
          <motion.div
            initial={{ scale: 0 }}
            whileInView={{ scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            viewport={{ once: true }}
            className="inline-flex items-center space-x-3 px-8 py-4 bg-gradient-to-r from-electric-blue/20 to-electric-cyan/20 border-2 border-electric-blue/50 rounded-full mb-12"
          >
            <i className="ri-shield-check-fill text-4xl text-electric-blue w-10 h-10 flex items-center justify-center"></i>
            <div className="text-left">
              <div className="font-orbitron text-2xl font-bold text-white">
                FULLY INSURED & BONDED
              </div>
              <div className="font-inter text-electric-blue text-sm">
                Up to $1,000,000 Coverage
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Badge Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {badges.map((badge, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.05, rotate: 5 }}
              className="group"
            >
              <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-6 hover:bg-white/15 hover:border-electric-blue/50 transition-all duration-300 hover:shadow-xl hover:shadow-electric-blue/20 text-center h-full flex flex-col items-center justify-center">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-electric-blue/20 to-electric-cyan/20 border border-electric-blue/30 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <i className={`${badge.icon} text-3xl text-electric-blue w-8 h-8 flex items-center justify-center`}></i>
                </div>
                <h3 className="font-orbitron text-base font-bold text-white mb-2">
                  {renderEditableField(index, 'name', badge.name, 'font-orbitron text-base font-bold text-white')}
                </h3>
                <div className="font-inter text-white/60 text-xs">
                  {renderEditableField(index, 'description', badge.description, 'font-inter text-white/60 text-xs')}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
